import { useCallback, useEffect, useMemo, useState } from "react";
import { Save } from "lucide-react";
import FacetFormField, {
  INPUT_CLASS,
} from "../../notebook-details/analytics/facet-form/FacetFormField";
import FacetFormDropdown from "../../notebook-details/analytics/facet-form/FacetFormDropdown";
import ChartConfigColorEntry from "./ChartConfigColorEntry";
import { get_color } from "../../../../report-visuals/report-utils";
import { normalizeColorInput } from "../tarsBarChartHelpers";
import {
  CHART_TYPE_OPTIONS,
  chartConfigColorSectionTitle,
  extractLabelsFromEntry,
  parseChartEntryToFormModel,
  serializeFormModelToChartEntry,
  type ChartConfigFormModel,
} from "./chart-config-form-helpers";

interface ChartConfigFormPanelProps {
  chartEntry: Record<string, unknown> | null;
  onSave: (updatedEntry: Record<string, unknown>) => void;
}

export default function ChartConfigFormPanel({
  chartEntry,
  onSave,
}: ChartConfigFormPanelProps) {
  const [model, setModel] = useState<ChartConfigFormModel>(() =>
    parseChartEntryToFormModel(chartEntry)
  );

  useEffect(() => {
    setModel(parseChartEntryToFormModel(chartEntry));
  }, [chartEntry]);

  const labels = useMemo(
    () => extractLabelsFromEntry(chartEntry, model.chartType),
    [chartEntry, model.chartType]
  );
  const colorSectionTitle = chartConfigColorSectionTitle(model.chartType);

  const update = useCallback((patch: Partial<ChartConfigFormModel>) => {
    setModel((prev) => ({ ...prev, ...patch }));
  }, []);

  const handleColorChange = useCallback((label: string, color: string) => {
    setModel((prev) => ({
      ...prev,
      colorByLabel: { ...prev.colorByLabel, [label]: normalizeColorInput(color) },
    }));
  }, []);

  const handleSave = () => {
    if (!chartEntry) return;
    const updated = serializeFormModelToChartEntry(model, chartEntry);
    onSave(updated);
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="flex w-full flex-col gap-5 px-3 py-4 sm:gap-6 sm:px-4 sm:py-5 lg:px-5">
          {/* Chart Type */}
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <h4 className="mb-3 text-[13px] font-bold text-slate-800">
              Chart Type
            </h4>
            <FacetFormField label="Choose type">
              <FacetFormDropdown
                value={model.chartType}
                options={CHART_TYPE_OPTIONS.map((o) => ({
                  value: o.id,
                  label: o.label,
                }))}
                onChange={(v) => update({ chartType: v })}
              />
            </FacetFormField>
          </div>

          {/* Text Styling */}
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <h4 className="mb-3 text-[13px] font-bold text-slate-800">
              Text Styling
            </h4>
            <div className="flex flex-col gap-4">
              <FacetFormField label="Title">
                <input
                  type="text"
                  value={model.title}
                  onChange={(e) => update({ title: e.target.value })}
                  placeholder="Chart title"
                  className={INPUT_CLASS}
                />
              </FacetFormField>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <FacetFormField label="Title font size (px)">
                  <input
                    type="number"
                    min={8}
                    max={72}
                    value={model.titleFontSize}
                    onChange={(e) =>
                      update({ titleFontSize: Number(e.target.value) })
                    }
                    className={INPUT_CLASS}
                  />
                </FacetFormField>
                <FacetFormField label="Axis font size (px)">
                  <input
                    type="number"
                    min={8}
                    max={72}
                    value={model.axisFontSize}
                    onChange={(e) =>
                      update({ axisFontSize: Number(e.target.value) })
                    }
                    className={INPUT_CLASS}
                  />
                </FacetFormField>
                <FacetFormField label="Data label font size (px)">
                  <input
                    type="number"
                    min={8}
                    max={72}
                    value={model.dataLabelFontSize}
                    onChange={(e) =>
                      update({ dataLabelFontSize: Number(e.target.value) })
                    }
                    className={INPUT_CLASS}
                  />
                </FacetFormField>
              </div>
              <FacetFormField label="Label color">
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={model.labelColor}
                    onChange={(e) => update({ labelColor: e.target.value })}
                    className="h-8 w-10 cursor-pointer rounded border border-slate-200 bg-transparent p-0.5"
                  />
                  <input
                    type="text"
                    value={model.labelColor}
                    onChange={(e) => update({ labelColor: e.target.value })}
                    className="w-[100px] rounded-md border border-slate-200 bg-slate-50 px-2 py-1.5 font-mono text-[12px] text-slate-600 outline-none focus:border-indigo-400 focus:ring-1 focus:ring-indigo-400/20"
                  />
                </div>
              </FacetFormField>
            </div>
          </div>

          {/* Bar / Slice Colors */}
          {labels.length > 0 && (
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <h4 className="mb-3 text-[13px] font-bold text-slate-800">
                {colorSectionTitle}
              </h4>
              <div className="flex flex-col gap-2">
                {labels.map((label, index) => (
                  <ChartConfigColorEntry
                    key={label}
                    label={label}
                    color={model.colorByLabel[label] ?? ""}
                    defaultColor={get_color(index)}
                    onChange={(c) => handleColorChange(label, c)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="flex shrink-0 items-center justify-end gap-2.5 border-t border-slate-200 bg-gradient-to-r from-slate-50 to-white px-5 py-3">
        <button
          type="button"
          onClick={handleSave}
          className="flex items-center gap-1.5 rounded-lg bg-indigo-600 px-4 py-2 text-[12px] font-semibold text-white shadow-sm transition hover:bg-indigo-700 active:bg-indigo-800"
        >
          <Save size={14} />
          Save Configuration
        </button>
      </div>
    </div>
  );
}
